import produce from 'immer';

import {
  IAddress,
  IVehicle,
  TVehicleVariables,
  TGenericVehicleVariables,
  TAZVehicleVariables,
  TSCVehicleVariables,
  TCAVehicleVariables,
  ERegionCode,
  IVehicleVariables,
  EVehicleOwnershipStatus,
} from '../../../interfaces';
import { getHiddenVehicleCoverageFields } from '../getHiddenVehicleCoverageFields';

function getRetrieveCoverageHelpers({
  variables,
  hiddenCoverages,
}: {
  variables: TVehicleVariables;
  hiddenCoverages: string[];
}) {
  function retrieveHiddenCoverageValue(
    key: keyof Pick<
      TVehicleVariables,
      'new_car_replacement' | 'gap' | 'oem_parts' | 'loan_lease_payoff'
    >
  ) {
    return hiddenCoverages.includes(key as string)
      ? false
      : (variables[key] as boolean);
  }

  return { retrieveHiddenCoverageValue };
}

function handleRegionVehicleVariables({
  baseVehicleYear,
  garageAddress,
  inStorage,
  isNewVehicle = false,
  ownershipStatus,
  regionCode,
  variables,
  year,
}: {
  baseVehicleYear?: number;
  garageAddress: IAddress;
  inStorage: IVehicle['in_storage'];
  isNewVehicle?: boolean;
  ownershipStatus: EVehicleOwnershipStatus;
  regionCode: ERegionCode;
  variables: TVehicleVariables;
  year: number;
}) {
  const hiddenCoverages = getHiddenVehicleCoverageFields(
    {
      garage_address: garageAddress,
      ownership_status: ownershipStatus,
      year,
    },
    { baseVehicleYear }
  );

  const { retrieveHiddenCoverageValue } = getRetrieveCoverageHelpers({
    variables,
    hiddenCoverages,
  });

  const hasComprehensiveDeductibleValue =
    isNewVehicle || !variables.comprehensive_deductible;
  const hasCollisionDeductibleValue =
    isNewVehicle || !variables.collision_deductible;

  switch (regionCode) {
    case 'CA':
      return produce(variables, (draft) => {
        if (['leased', 'financed'].includes(ownershipStatus)) {
          draft.comprehensive_deductible = hasComprehensiveDeductibleValue
            ? '1000'
            : variables.comprehensive_deductible;
          draft.collision_deductible = hasCollisionDeductibleValue
            ? '1000'
            : variables.collision_deductible;
        }

        if (inStorage) {
          draft.comprehensive_deductible = hasComprehensiveDeductibleValue
            ? '100'
            : variables.comprehensive_deductible;
          draft.collision_deductible = null;
          draft.collision_deductible_waiver = false;
          draft.rental_reimbursement = null;
        }
      }) as TCAVehicleVariables;
    case 'CO':
    case 'GA':
    case 'FL':
    case 'IL':
    case 'IN':
    case 'KS':
    case 'MO':
    case 'OR':
    case 'OH':
    case 'PA':
    case 'TN':
    case 'TX':
    case 'UT':
    case 'VA':
    case 'WI':
      return produce(variables, (draft) => {
        draft.new_car_replacement = retrieveHiddenCoverageValue(
          'new_car_replacement'
        );
        draft.gap = retrieveHiddenCoverageValue('gap');
        draft.oem_parts = retrieveHiddenCoverageValue('oem_parts');

        draft.side_hustle = variables.comprehensive_deductible
          ? variables.side_hustle
          : false;

        if (['leased', 'financed'].includes(ownershipStatus)) {
          draft.comprehensive_deductible = hasComprehensiveDeductibleValue
            ? '1000'
            : variables.comprehensive_deductible;
          draft.collision_deductible = hasCollisionDeductibleValue
            ? '1000'
            : variables.collision_deductible;
        }
      }) as TGenericVehicleVariables;
    case 'AZ':
      return produce(variables, (draft) => {
        draft.new_car_replacement = retrieveHiddenCoverageValue(
          'new_car_replacement'
        );
        draft.gap = retrieveHiddenCoverageValue('gap');
        draft.oem_parts = retrieveHiddenCoverageValue('oem_parts');

        draft.side_hustle = variables.comprehensive_deductible
          ? variables.side_hustle
          : false;

        if (['leased', 'financed'].includes(ownershipStatus)) {
          draft.comprehensive_deductible = hasComprehensiveDeductibleValue
            ? '1000'
            : variables.comprehensive_deductible;
          draft.collision_deductible = hasCollisionDeductibleValue
            ? '1000'
            : variables.collision_deductible;
        }
      }) as TAZVehicleVariables;
    case 'SC':
    default:
      return produce(variables, (draft) => {
        draft.loan_lease_payoff =
          retrieveHiddenCoverageValue('loan_lease_payoff');
        draft.new_car_replacement = retrieveHiddenCoverageValue(
          'new_car_replacement'
        );
        draft.oem_parts = retrieveHiddenCoverageValue('oem_parts');

        if (['leased', 'financed'].includes(ownershipStatus)) {
          draft.comprehensive_deductible = hasComprehensiveDeductibleValue
            ? '1000'
            : variables.comprehensive_deductible;
          draft.collision_deductible = hasCollisionDeductibleValue
            ? '1000'
            : variables.collision_deductible;
        }
      }) as TSCVehicleVariables;
  }
}

/**
 * Updates a vehicle's variables based on factors like region code or ownership status.
 * Used when updating or adding a vehicle.
 * @private
 * @param params The vehicle's variables and additional context.
 */
export default function getUpdatedVehicleVariables(params: {
  baseVehicleYear?: number;
  garageAddress: IAddress;
  inStorage: IVehicle['in_storage'];
  isNewVehicle?: boolean;
  ownershipStatus: EVehicleOwnershipStatus;
  variables?: TVehicleVariables;
  year: number;
}) {
  const { ownershipStatus, variables, garageAddress } = params;
  const {
    region: { code: regionCode },
  } = garageAddress;

  const isLeasedOrFinanced = ['leased', 'financed'].includes(ownershipStatus);
  const isVehicleWithoutVariables = !variables;
  const isUpdatedLeasedVehicleWithoutCompColl =
    isLeasedOrFinanced && !variables?.comprehensive_deductible;

  if (!variables) {
    return isLeasedOrFinanced
      ? {
          comprehensive_deductible: '1000',
          collision_deductible: '1000',
        }
      : {};
  }

  const vehicleVariables = handleRegionVehicleVariables({
    ...params,
    variables,
    regionCode,
  }) as IVehicleVariables;

  if (isUpdatedLeasedVehicleWithoutCompColl && !isVehicleWithoutVariables) {
    const variables = produce(vehicleVariables, (draft) => {
      draft.comprehensive_deductible = '1000';
      draft.collision_deductible = '1000';
    });
    return variables;
  }
  return vehicleVariables;
}
